import * as React from 'react';

/**
 * Shared option type for CheckboxGroup and RadioGroup.
 */
export interface GroupOption {
  /** The value of the option */
  value: string;
  /** The label displayed for the option */
  label: string;
  /** Whether this specific option is disabled */
  disabled?: boolean;
}

/**
 * Shared base props for CheckboxGroup and RadioGroup.
 */
export interface BaseGroupProps {
  /** Label for the group */
  label?: string;
  /** Array of options to display */
  options: GroupOption[];
  /** Layout orientation of the controls */
  orientation?: "horizontal" | "vertical";
  /** Whether the group is in an error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Whether the group is required */
  required?: boolean;
  /** Whether all controls in the group are disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Helper text displayed below the group */
  helperText?: string;
  /** ID for the group (used for accessibility) */
  id?: string;
}

/**
 * Configuration options for useGroupState.
 */
export interface UseGroupStateOptions<T, V extends T = T> {
  prefix: string;
  providedId: string | undefined;
  controlledValue: T | undefined;
  defaultValue: T;
  onChange: ((value: V) => void) | undefined;
  error: boolean;
  errorMessage: string | undefined;
  helperText: string | undefined;
}

/**
 * Hook: common state management for groups.
 *
 * @typeParam T - The state value type (e.g. `string[]` or `string | undefined`)
 * @typeParam V - The value type emitted to onChange; defaults to T.
 *   Useful when the internal state is wider than the onChange parameter
 *   (e.g. state is `string | undefined` but onChange only receives `string`).
 */
export function useGroupState<T, V extends T = T>(
  options: UseGroupStateOptions<T, V>,
): {
  groupId: string;
  helperId: string;
  currentValue: T;
  setValue: (value: V) => void;
  showError: string | false | undefined;
  message: string | undefined;
  messageId: string | undefined;
} {
  const { prefix, providedId, controlledValue, defaultValue, onChange, error, errorMessage, helperText } = options;

  const reactId = React.useId();
  const groupId = providedId || `${prefix}-${reactId}`;
  const helperId = `${prefix}-helper-${reactId}`;

  const [internalValue, setInternalValue] = React.useState<T>(defaultValue);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const setValue = React.useCallback(
    (newValue: V) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  const { showError, message, messageId } = computeGroupMessage(
    error, errorMessage, helperText, helperId
  );

  return {
    groupId, helperId, currentValue,
    setValue, showError, message, messageId,
  };
}

/**
 * Build CSS classes for the options container in a group component.
 */
export function buildOptionsContainerClasses(orientation: 'horizontal' | 'vertical'): string {
  return [
    'flex',
    orientation === 'vertical' ? 'flex-col gap-3' : 'flex-row flex-wrap gap-4',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Build CSS classes for the fieldset wrapper.
 */
export function buildFieldsetClasses(className: string): string {
  return ['w-full', className].filter(Boolean).join(' ');
}

/**
 * Compute the message to display and its ID for group components.
 */
export function computeGroupMessage(
  error: boolean,
  errorMessage: string | undefined,
  helperText: string | undefined,
  helperId: string,
): { showError: string | false | undefined; message: string | undefined; messageId: string | undefined } {
  const showError = error && errorMessage;
  const message = showError ? errorMessage : helperText;
  const messageId = message ? helperId : undefined;
  return { showError, message, messageId };
}
